import classNames from 'classnames';
import React, { useContext, useState } from 'react';

import { ArrowAngleDown } from '@zooplus/icons';
import { LayoutContainer } from '@zooplus/ui-components';

import AppContext from '../../context';
import { UiCategory, UiCategoryExt } from '../../types';
import CategoryFlyout from '../CategoryFlyout/CategoryFlyout';
import { DarkOverlay } from '../DarkOverlay/DarkOverlay';
import { ThemedIcon } from '../Icon/Icon';

import styles from './LowerBar.module.scss';

export const ENTER_DELAY = 150;
export const LEAVE_DELAY = 300;

type FlyoutMapValue = {
    node: React.RefObject<HTMLDivElement | null>;
    menu: React.RefObject<HTMLLIElement | null>;
    show: boolean;
};

type FlyoutMap = Map<number, FlyoutMapValue>;

export default function LowerMenu(): React.JSX.Element {
    const { categories, showCategories, translations } = useContext(AppContext);
    const accessibleLabel = translations.get(
        'shop.header.main.navigation.category.accessible.button.label',
    );

    let onCategoryMouseEnterTimeoutID: NodeJS.Timeout | null = null;
    let onCategoryMouseLeaveTimeoutID: NodeJS.Timeout | null = null;

    const [flyoutsMap, setFlyoutsMap] = useState<FlyoutMap>(
        new Map(
            categories.map((_category: UiCategory, index: number) => [
                index,
                {
                    node: React.createRef<HTMLDivElement>(),
                    menu: React.createRef<HTMLLIElement>(),
                    show: false,
                },
            ]),
        ),
    );

    const showOverlay = !!Array.from(flyoutsMap.values()).find(
        (value) => value.show === true,
    );

    const onCategoryItemMouseEnter = (index: number, callback = () => {}) => {
        if (onCategoryMouseLeaveTimeoutID !== null) {
            clearTimeout(onCategoryMouseLeaveTimeoutID);
        }
        onCategoryMouseEnterTimeoutID = setTimeout(() => {
            const entries: [number, FlyoutMapValue][] = categories.map(
                (_category: UiCategory, i: number) => [
                    i,
                    {
                        node: React.createRef<HTMLDivElement>(),
                        menu: React.createRef<HTMLLIElement>(),
                        show: index === i,
                    },
                ],
            );
            setFlyoutsMap(new Map(entries));
            callback();
        }, ENTER_DELAY);
    };

    const onCategoryFlyoutMouseEnter = () => {
        if (onCategoryMouseLeaveTimeoutID !== null) {
            clearTimeout(onCategoryMouseLeaveTimeoutID);
        }
    };

    const onCategoryMouseLeave = () => {
        if (onCategoryMouseEnterTimeoutID !== null) {
            clearTimeout(onCategoryMouseEnterTimeoutID);
        }
        onCategoryMouseLeaveTimeoutID = setTimeout(() => {
            const entries: [number, FlyoutMapValue][] = categories.map(
                (_category: UiCategory, i: number) => [
                    i,
                    {
                        node: React.createRef<HTMLDivElement>(),
                        menu: React.createRef<HTMLLIElement>(),
                        show: false,
                    },
                ],
            );
            setFlyoutsMap(new Map(entries));
        }, LEAVE_DELAY);
    };

    const onAccessibleButtonClick = (index: number) => {
        onCategoryItemMouseEnter(index, () => {
            const flyoutItem = flyoutsMap.get(index);
            if (!flyoutItem) return;
            const { node } = flyoutItem;
            if (node.current) {
                const link = node.current.querySelector('a');
                if (link) link.focus();
            }
        });
    };

    const hideAllFlyouts = (callback = () => {}) => {
        const entries: [number, FlyoutMapValue][] = categories.map(
            (_category: UiCategory, i: number) => [
                i,
                {
                    node: React.createRef(),
                    menu: React.createRef(),
                    show: false,
                },
            ],
        );
        setFlyoutsMap(new Map(entries));
        callback();
    };

    const hideAllFlyoutsAndFocus = (
        index = 0,
        target: 'a' | 'button' = 'a',
    ) => {
        // eslint-disable-next-line consistent-return
        hideAllFlyouts(() => {
            const flyoutItem = flyoutsMap.get(index);
            if (!flyoutItem) return null;
            const { menu } = flyoutItem;
            if (menu.current) {
                const link = menu.current.querySelector(target);
                if (link) link.focus();
            }
        });
    };

    if (!showCategories) {
        return (
            <div
                data-zta="lower-bar-empty"
                className={styles.desktopEmptyNav}
            />
        );
    }

    return (
        <nav>
            <LayoutContainer
                variant="default"
                className={styles.desktopNavigation}
                data-zta="lower-bar"
                id="header-category-links"
                aria-label={translations.get(
                    'shop.header.main.navigation.title',
                )}>
                <div
                    data-zta="lower-bar-category-slider"
                    className={styles.categorySlider}>
                    <ul
                        className={styles.categoryBar}
                        data-zta="lower-bar-categories">
                        {categories.map(
                            (category: UiCategoryExt, index: number) => {
                                const flyoutItem = flyoutsMap.get(index);
                                if (!flyoutItem) return null;
                                const { title, path } = category;
                                const { show, menu } = flyoutItem;
                                return (
                                    <li
                                        className={styles.categoryItem}
                                        data-zta="lower-bar-category-item"
                                        key={title}
                                        ref={menu}
                                        onMouseEnter={() =>
                                            onCategoryItemMouseEnter(index)
                                        }
                                        onMouseLeave={onCategoryMouseLeave}>
                                        <a
                                            className={classNames(
                                                styles.categoryItemLink,
                                                {
                                                    [styles.categoryItemLinkActive]:
                                                        category.active,
                                                },
                                            )}
                                            data-zta="lower-bar-category-item-link"
                                            href={path}>
                                            {title}
                                        </a>
                                        <button
                                            aria-expanded={show}
                                            aria-labelledby={`category-button-label-${title}`}
                                            className={
                                                styles.categoryItemButton
                                            }
                                            data-zta="lower-bar-category-item-btn"
                                            onClick={() =>
                                                onAccessibleButtonClick(index)
                                            }
                                            type="button">
                                            <ThemedIcon
                                                Icon={ArrowAngleDown}
                                                width="18"
                                                height="18"
                                                viewBox="3 3 18 18"
                                                aria-labelledby={`category-button-label-${title}`}
                                                color={'neutralDark'}
                                            />
                                            <div
                                                className={
                                                    styles.accessibleText
                                                }
                                                id={`category-button-label-${title}`}>{`${accessibleLabel}: ${title}`}</div>
                                        </button>
                                    </li>
                                );
                            },
                        )}
                    </ul>
                </div>
                {categories.map((category: UiCategory, index: number) => {
                    const { node, show } = flyoutsMap.get(index)!;

                    const onBlurFirst = () => {
                        hideAllFlyoutsAndFocus(index, 'button');
                    };

                    const onBlurLast = () => {
                        const isLastFlyout = index === flyoutsMap.size - 1;
                        if (!isLastFlyout) {
                            hideAllFlyoutsAndFocus(index + 1, 'a');
                        } else {
                            hideAllFlyouts();
                        }
                    };

                    return (
                        <CategoryFlyout
                            key={category.title}
                            ref={node}
                            show={show}
                            category={category}
                            onMouseEnter={onCategoryFlyoutMouseEnter}
                            onMouseLeave={onCategoryMouseLeave}
                            onBlurFirst={onBlurFirst}
                            onBlurLast={onBlurLast}
                        />
                    );
                })}
                <DarkOverlay show={showOverlay} />
            </LayoutContainer>
        </nav>
    );
}
